import { Box, IconButton, Tab, Tabs } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import DollarBagIcon from "../assets/icon/DollarBagIcon";
import PatientIcon from "../assets/icon/PatientIcon";
import PhoneIcon from "../assets/icon/PhoneIcon";
import StablePatientIcon from "../assets/icon/StablePatientIcon";

import ReferralTableReceived from "./Reffral/ReferralTableReceived";
import ReffralTable from "./Reffral/ReffralTable";
import { SearchInput } from "./shared/search";
import { StatsCard } from "./shared/statscard";
import { useAuth } from "../context/AuthContext";
import { Referral } from "./Reffral/types/referral";
import RefreshIcon from "../assets/icon/RefreshIcon";

/* =====================
   Helpers
===================== */
const calculateStats = (referrals: Referral[]) => {
  const uniquePatients = new Set(referrals.map((r) => r.patient_id));

  return {
    totalPatients: referrals.length,
    contacted: referrals.filter((r) => r.status !== "Pending").length,
    converted: referrals.filter((r) => r.status === "Completed").length,
    totalRevenue: 0, // backend not available
  };
};

const filterBySearch = (referrals: Referral[], search: string) => {
  if (!search) return referrals;

  const q = search.toLowerCase();

  return referrals.filter(
    (r) =>
      r.patient?.email?.toLowerCase().includes(q) ||
      r.patient?.username?.toLowerCase().includes(q) ||
      r.from_provider?.provider?.first_name?.toLowerCase().includes(q) ||
      r.from_provider?.provider?.last_name?.toLowerCase().includes(q)
  );
};

/* =====================
   Component
===================== */
export const ReferralLayout = () => {
  const { user } = useAuth();
  const token = user?.token;

  const [tabIndex, setTabIndex] = useState(0);
  const [searchText, setSearchText] = useState("");

  const [receivedReferrals, setReceivedReferrals] = useState<Referral[]>([]);
  const [sentReferrals, setSentReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(false);

 const fetchReferrals = async () => {
    try {
      if (!user?.id || !token) return;

      setLoading(true);

      const [receivedRes, sentRes] = await Promise.all([
        axios.get(
          `https://vivotal-backend.vercel.app/api/v1/referrals?received_by=${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `https://vivotal-backend.vercel.app/api/v1/referrals?sent_by=${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      setReceivedReferrals(receivedRes.data?.data || []);
      setSentReferrals(sentRes.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch referrals", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, [user?.id, token]);


  /* =====================
     Filter by search
  ===================== */
  const filteredReceived = useMemo(
    () => filterBySearch(receivedReferrals, searchText),
    [receivedReferrals, searchText]
  );

  const filteredSent = useMemo(
    () => filterBySearch(sentReferrals, searchText),
    [sentReferrals, searchText]
  );

    const handleRefresh = () => {
    fetchReferrals();
  };

  /* =====================
     Stats by tab
  ===================== */
  const stats = useMemo(() => {
    return tabIndex === 0
      ? calculateStats(filteredReceived)
      : calculateStats(filteredSent);
  }, [tabIndex, filteredReceived, filteredSent]);

  return (
    <div className="section" style={{ width: "100%" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div className="heading">Referral</div>

        <div style={{ display: "flex", gap: 16 }}>
          {/* <button className="outline-bttn">
            <ExportIcon width={24} height={24} /> Export
          </button> */}
          {/* <button className="success-bttn">
            <PatientIcon width={24} height={24} /> Add Patient
          </button> */}
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh statistics"
            sx={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              padding: "8px",
              "&:hover": {
                borderColor: "#CBD5E0",
                backgroundColor: "#F7FAFC",
              },
            }}
          >
            <RefreshIcon width={20} height={20} />
          </IconButton>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
        <StatsCard
          count={stats.totalPatients}
          label="Total Patients"
          icon={<PatientIcon width={24} height={24} color="#A0AEC0" />}
          color="#4FD1C5"
        />
        <StatsCard
          count={stats.contacted}
          label="Contacted"
          icon={<PhoneIcon width={24} height={24} color="#A0AEC0" />}
          color="#F6AD55"
        />
        <StatsCard
          count={stats.converted}
          label="Converted"
          icon={<StablePatientIcon width={24} height={24} color="#A0AEC0" />}
          color="#1FB551"
        />
        <StatsCard
          count={`₹${stats.totalRevenue}`}
          label="Total Revenue"
          icon={<DollarBagIcon width={24} height={24} color="#A0AEC0" />}
          color="#0F4AEE"
        />
      </div>

      {/* Search */}
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "end" }}>
        <SearchInput value={searchText} onChange={setSearchText} />
      </div>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 2 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Referral Received" />
          <Tab label="Referral Sent" />
        </Tabs>
      </Box>

      {/* Tables */}
      {tabIndex === 0 && <ReferralTableReceived data={filteredReceived} />}

      {tabIndex === 1 && <ReffralTable data={filteredSent} />}
    </div>
  );
};
